import { useState, useMemo } from 'react';
import { totalFormulaExamples, masterFormula } from '../data';

export default function TotalFormulaSection() {
  const [spreadPips, setSpreadPips] = useState(1.2);
  const [lotSize, setLotSize] = useState(1.0);
  const [pipValue, setPipValue] = useState(10);
  const [commissionPerSide, setCommissionPerSide] = useState(3.50);
  const [isPerSide, setIsPerSide] = useState(true);
  const [swapRate, setSwapRate] = useState(-4.50);
  const [daysHeld, setDaysHeld] = useState(1);
  const [activePersona, setActivePersona] = useState<number | null>(null);

  const spreadCost = spreadPips * lotSize * pipValue;
  const commissionMultiplier = isPerSide ? 2 : 1;
  const commissionCost = commissionPerSide * commissionMultiplier * lotSize;
  const swapCost = swapRate * lotSize * daysHeld;
  const totalCost = spreadCost + commissionCost + swapCost;

  const pcts = useMemo(() => {
    const absTotal = Math.abs(spreadCost) + Math.abs(commissionCost) + Math.abs(swapCost);
    if (absTotal === 0) return { spread: 0, commission: 0, swap: 0 };
    return {
      spread: (Math.abs(spreadCost) / absTotal) * 100,
      commission: (Math.abs(commissionCost) / absTotal) * 100,
      swap: (Math.abs(swapCost) / absTotal) * 100,
    };
  }, [spreadCost, commissionCost, swapCost]);

  const breakevenPips = pipValue * lotSize > 0 ? totalCost / (pipValue * lotSize) : 0;

  const dominantCost = pcts.spread > pcts.commission && pcts.spread > pcts.swap ? 'Spread'
    : pcts.commission > pcts.swap ? 'Commission' : 'Swap';
  const dominantPctVal = dominantCost === 'Spread' ? pcts.spread
    : dominantCost === 'Commission' ? pcts.commission : pcts.swap;

  const applyPersona = (idx: number) => {
    const p = totalFormulaExamples[idx];
    setSpreadPips(p.spreadPips);
    setLotSize(p.lotSize);
    setPipValue(p.pipValuePerLot);
    setCommissionPerSide(p.commissionPerSide);
    setIsPerSide(p.isPerSide);
    setSwapRate(p.swapRate);
    setDaysHeld(p.daysHeld);
    setActivePersona(idx);
  };

  return (
    <section id="total-formula" className="relative w-full bg-foreground-950 overflow-hidden">
      <div className="w-full h-px bg-gradient-to-r from-transparent via-foreground-800/40 to-transparent" />

      <div className="w-full max-w-main mx-auto px-4 md:px-6 py-12 md:py-18">

        {/* Step Header */}
        <div className="mb-10 md:mb-16">
          <h2 className="text-[28px] md:text-[52px] font-[750] leading-[1.02] text-foreground-50 tracking-[-0.03em] max-w-[780px]">
            Total Trade Cost
          </h2>

          {/* Formula as large editorial typography */}
          <div className="mt-8 md:mt-10">
            <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-foreground-400 mb-4 md:mb-5">
              The Formula
            </div>

            <div className="space-y-2 md:space-y-3">
              {/* Line 1: Total Cost = */}
              <div className="flex items-baseline gap-2 md:gap-3 flex-wrap">
                <span className="text-[28px] md:text-[44px] font-[750] text-foreground-50 tracking-[-0.02em] leading-none">
                  Total Cost
                </span>
                <span className="text-[22px] md:text-[32px] font-[200] text-foreground-600 leading-none">
                  =
                </span>
              </div>

              {/* Line 2: Spread Cost + Commission + Swap */}
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-[24px] md:text-[40px] font-[750] text-primary-500 tracking-[-0.02em] leading-none">
                  Spread Cost
                </span>
                <span className="text-[18px] md:text-[22px] font-[300] text-foreground-600 leading-none">+</span>
                <span className="text-[24px] md:text-[40px] font-[750] text-accent-500 tracking-[-0.02em] leading-none">
                  Commission
                </span>
                <span className="text-[18px] md:text-[22px] font-[300] text-foreground-600 leading-none">+</span>
                <span className="text-[24px] md:text-[40px] font-[750] text-secondary-500 tracking-[-0.02em] leading-none">
                  Swap
                </span>
              </div>
            </div>

            {/* Tagline below formula */}
            <p className="text-[13px] md:text-[14px] text-foreground-300 mt-4 md:mt-5 max-w-[560px] leading-relaxed">
              {masterFormula.description}
            </p>
          </div>

          {/* Divider */}
          <div className="w-full h-px bg-foreground-800/40 mt-10 md:mt-12 mb-8 md:mb-10" />

          {/* Persona quick-select */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-8 md:mb-10 relative z-10">
            {totalFormulaExamples.map((p, i) => (
              <button
                key={i}
                onClick={() => applyPersona(i)}
                className={`text-left px-3 md:px-4 py-3 md:py-3.5 rounded-xl border transition-all duration-200 cursor-pointer ${
                  activePersona === i
                    ? 'border-accent-500/60 bg-accent-500/12 ring-1 ring-accent-500/25'
                    : 'border-foreground-800/50 bg-foreground-900/50 hover:border-foreground-700/60 hover:bg-foreground-900'
                }`}
              >
                <div className={`text-[12px] md:text-[13px] font-[700] leading-snug ${activePersona === i ? 'text-accent-400' : 'text-foreground-200'}`}>
                  {p.persona}
                </div>
                <div className={`text-[10px] md:text-[11px] mt-0.5 ${activePersona === i ? 'text-accent-500/40' : 'text-foreground-500'}`}>
                  {p.pair} · {p.lotSize}L
                </div>
                <div className={`mt-1.5 text-[18px] md:text-[20px] font-[800] font-mono tracking-tight ${
                  p.dominantCost === 'Swap' ? 'text-secondary-400' : p.dominantCost === 'Commission' ? 'text-accent-400' : 'text-primary-400'
                }`}>
                  ${p.totalCost.toFixed(2)}
                </div>
              </button>
            ))}
          </div>

          {/* Main Grid: Controls + Results */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_440px] gap-8 lg:gap-12">

            {/* ========== LEFT: Master Control Panel ========== */}
            <div>
              <div className="bg-foreground-900 rounded-2xl overflow-hidden">
                {/* Terminal header */}
                <div className="flex items-center justify-between px-5 py-3 border-b border-foreground-800/40">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-foreground-600" />
                      <div className="w-2 h-2 rounded-full bg-foreground-600" />
                      <div className="w-2 h-2 rounded-full bg-foreground-600" />
                    </div>
                    <span className="text-[12px] font-[600] text-foreground-400 uppercase tracking-[0.08em] ml-2.5">
                      Cost Controls
                    </span>
                  </div>
                  <span className="text-[10px] text-foreground-600 font-mono">all-params</span>
                </div>

                <div className="p-5 md:p-6 space-y-5">

                  {/* --- Spread Cost --- */}
                  <div>
                    <div className="flex items-center justify-between mb-2.5">
                      <div className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary-500" />
                        <span className="text-[11px] font-[700] text-foreground-300 uppercase tracking-[0.08em]">Spread Cost</span>
                      </div>
                      <span className="text-[20px] font-[750] text-primary-400 leading-none font-mono tracking-tight">
                        ${spreadCost.toFixed(2)}
                      </span>
                    </div>
                    <div className="text-[11px] text-foreground-500 font-mono mb-3">
                      {spreadPips.toFixed(1)}p · {lotSize.toFixed(2)}L · ${pipValue.toFixed(0)}/p
                    </div>
                    <div className="relative h-[22px] flex items-center">
                      <div className="absolute inset-x-0 h-1 rounded-full bg-foreground-800/50 overflow-hidden pointer-events-none">
                        <div className="h-full rounded-full bg-primary-500 transition-all duration-200" style={{ width: `${(spreadPips / 5.0) * 100}%` }} />
                      </div>
                      <input
                        type="range" min="0.0" max="5.0" step="0.1" value={spreadPips}
                        onChange={(e) => { setSpreadPips(parseFloat(e.target.value)); setActivePersona(null); }}
                        className="absolute inset-0 w-full h-full appearance-none cursor-pointer bg-transparent z-10
                          [&::-webkit-slider-runnable-track]:bg-transparent
                          [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-[16px] [&::-webkit-slider-thumb]:h-[16px] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-primary-500 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-[0_0_0_4px_rgba(255,255,255,0.04)] [&::-webkit-slider-thumb]:transition-shadow [&::-webkit-slider-thumb]:hover:shadow-[0_0_0_8px_rgba(255,255,255,0.08)]
                          [&::-moz-range-track]:bg-transparent [&::-moz-range-track]:h-1
                          [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-[16px] [&::-moz-range-thumb]:h-[16px] [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-primary-500 [&::-moz-range-thumb]:cursor-pointer"
                      />
                    </div>
                    <div className="flex justify-between -mt-0.5">
                      <span className="text-[10px] text-foreground-600 font-mono">0 pips</span>
                      <span className="text-[10px] text-foreground-600 font-mono">5 pips</span>
                    </div>
                  </div>

                  <div className="w-full h-px bg-foreground-800/30" />

                  {/* --- Commission --- */}
                  <div>
                    <div className="flex items-center justify-between mb-2.5">
                      <div className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-accent-500" />
                        <span className="text-[11px] font-[700] text-foreground-300 uppercase tracking-[0.08em]">Commission</span>
                      </div>
                      <div className="flex items-center gap-2.5">
                        <div className="flex items-center bg-foreground-800/60 rounded-full p-0.5">
                          <button
                            onClick={() => { setIsPerSide(true); setActivePersona(null); }}
                            className={`px-2.5 py-1 rounded-full text-[10px] font-[600] transition-all cursor-pointer whitespace-nowrap ${
                              isPerSide ? 'bg-accent-500/20 text-accent-400' : 'text-foreground-500 hover:text-foreground-400'
                            }`}
                          >
                            Per Side
                          </button>
                          <button
                            onClick={() => { setIsPerSide(false); setActivePersona(null); }}
                            className={`px-2.5 py-1 rounded-full text-[10px] font-[600] transition-all cursor-pointer whitespace-nowrap ${
                              !isPerSide ? 'bg-accent-500/20 text-accent-400' : 'text-foreground-500 hover:text-foreground-400'
                            }`}
                          >
                            RT
                          </button>
                        </div>
                        <span className="text-[20px] font-[750] text-accent-400 leading-none font-mono tracking-tight">
                          ${commissionCost.toFixed(2)}
                        </span>
                      </div>
                    </div>
                    <div className="text-[11px] text-foreground-500 font-mono mb-3">
                      ${commissionPerSide.toFixed(2)}/{isPerSide ? 'side' : 'RT'} · ×{commissionMultiplier} · {lotSize.toFixed(2)}L
                    </div>
                    <div className="relative h-[22px] flex items-center">
                      <div className="absolute inset-x-0 h-1 rounded-full bg-foreground-800/50 overflow-hidden pointer-events-none">
                        <div className="h-full rounded-full bg-accent-500 transition-all duration-200" style={{ width: `${(commissionPerSide / 15) * 100}%` }} />
                      </div>
                      <input
                        type="range" min="0" max="15" step="0.5" value={commissionPerSide}
                        onChange={(e) => { setCommissionPerSide(parseFloat(e.target.value)); setActivePersona(null); }}
                        className="absolute inset-0 w-full h-full appearance-none cursor-pointer bg-transparent z-10
                          [&::-webkit-slider-runnable-track]:bg-transparent
                          [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-[16px] [&::-webkit-slider-thumb]:h-[16px] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-accent-500 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-[0_0_0_4px_rgba(255,255,255,0.04)] [&::-webkit-slider-thumb]:transition-shadow [&::-webkit-slider-thumb]:hover:shadow-[0_0_0_8px_rgba(255,255,255,0.08)]
                          [&::-moz-range-track]:bg-transparent [&::-moz-range-track]:h-1
                          [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-[16px] [&::-moz-range-thumb]:h-[16px] [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-accent-500 [&::-moz-range-thumb]:cursor-pointer"
                      />
                    </div>
                    <div className="flex justify-between -mt-0.5">
                      <span className="text-[10px] text-foreground-600 font-mono">$0</span>
                      <span className="text-[10px] text-foreground-600 font-mono">$15</span>
                    </div>
                  </div>

                  <div className="w-full h-px bg-foreground-800/30" />

                  {/* --- Swap --- */}
                  <div>
                    <div className="flex items-center justify-between mb-2.5">
                      <div className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-secondary-500" />
                        <span className="text-[11px] font-[700] text-foreground-300 uppercase tracking-[0.08em]">Swap</span>
                      </div>
                      <span className={`text-[20px] font-[750] leading-none font-mono tracking-tight ${swapCost >= 0 ? 'text-accent-400' : 'text-secondary-400'}`}>
                        {swapCost >= 0 ? '+' : ''}${swapCost.toFixed(2)}
                      </span>
                    </div>
                    <div className="text-[11px] text-foreground-500 font-mono mb-4">
                      {swapRate >= 0 ? '+' : ''}${swapRate.toFixed(2)}/day · {lotSize.toFixed(2)}L · {daysHeld}d
                    </div>

                    <div className="grid grid-cols-2 gap-5">
                      {/* Swap Rate */}
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-[9px] font-[600] text-foreground-500 uppercase tracking-[0.06em]">Rate</span>
                          <span className={`text-[14px] font-[700] font-mono ${swapRate >= 0 ? 'text-accent-400' : 'text-secondary-400'}`}>
                            {swapRate >= 0 ? '+' : ''}${swapRate.toFixed(2)}
                          </span>
                        </div>
                        <div className="relative h-[20px] flex items-center">
                          <div className="absolute inset-x-0 h-1 rounded-full bg-foreground-800/50 overflow-hidden pointer-events-none">
                            <div className={`h-full rounded-full transition-all duration-200 ${swapRate >= 0 ? 'bg-accent-500' : 'bg-secondary-500'}`}
                              style={{ width: `${((swapRate + 50) / 60) * 100}%` }} />
                          </div>
                          <input
                            type="range" min="-50" max="10" step="0.5" value={swapRate}
                            onChange={(e) => { setSwapRate(parseFloat(e.target.value)); setActivePersona(null); }}
                            className="absolute inset-0 w-full h-full appearance-none cursor-pointer bg-transparent z-10
                              [&::-webkit-slider-runnable-track]:bg-transparent
                              [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-[14px] [&::-webkit-slider-thumb]:h-[14px] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-secondary-500 [&::-webkit-slider-thumb]:cursor-pointer
                              [&::-moz-range-track]:bg-transparent [&::-moz-range-track]:h-1
                              [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-[14px] [&::-moz-range-thumb]:h-[14px] [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-secondary-500 [&::-moz-range-thumb]:cursor-pointer"
                          />
                        </div>
                        <div className="flex justify-between -mt-0.5">
                          <span className="text-[9px] text-foreground-600 font-mono">-$50</span>
                          <span className="text-[9px] text-foreground-600 font-mono">$10</span>
                        </div>
                      </div>

                      {/* Days Held */}
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-[9px] font-[600] text-foreground-500 uppercase tracking-[0.06em]">Days Held</span>
                          <span className="text-[14px] font-[700] text-foreground-200 font-mono">{daysHeld}</span>
                        </div>
                        <div className="relative h-[20px] flex items-center">
                          <div className="absolute inset-x-0 h-1 rounded-full bg-foreground-800/50 overflow-hidden pointer-events-none">
                            <div className="h-full rounded-full bg-secondary-500 transition-all duration-200" style={{ width: `${(daysHeld / 60) * 100}%` }} />
                          </div>
                          <input
                            type="range" min="0" max="60" step="1" value={daysHeld}
                            onChange={(e) => { setDaysHeld(parseInt(e.target.value)); setActivePersona(null); }}
                            className="absolute inset-0 w-full h-full appearance-none cursor-pointer bg-transparent z-10
                              [&::-webkit-slider-runnable-track]:bg-transparent
                              [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-[14px] [&::-webkit-slider-thumb]:h-[14px] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-secondary-500 [&::-webkit-slider-thumb]:cursor-pointer
                              [&::-moz-range-track]:bg-transparent [&::-moz-range-track]:h-1
                              [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-[14px] [&::-moz-range-thumb]:h-[14px] [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-secondary-500 [&::-moz-range-thumb]:cursor-pointer"
                          />
                        </div>
                        <div className="flex justify-between -mt-0.5">
                          <span className="text-[9px] text-foreground-600 font-mono">0</span>
                          <span className="text-[9px] text-foreground-600 font-mono">60</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="w-full h-px bg-foreground-800/30" />

                  {/* --- Shared: Lot Size + Pip Value --- */}
                  <div className="grid grid-cols-2 gap-5">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] font-[600] text-foreground-500 uppercase tracking-[0.06em]">Lot Size</span>
                        <span className="text-[16px] font-[700] text-foreground-200 font-mono">{lotSize.toFixed(2)}</span>
                      </div>
                      <div className="relative h-[20px] flex items-center">
                        <div className="absolute inset-x-0 h-1 rounded-full bg-foreground-800/50 overflow-hidden pointer-events-none">
                          <div className="h-full rounded-full bg-foreground-500/60 transition-all duration-200" style={{ width: `${(lotSize / 5.0) * 100}%` }} />
                        </div>
                        <input
                          type="range" min="0.01" max="5.0" step="0.01" value={lotSize}
                          onChange={(e) => { setLotSize(parseFloat(e.target.value)); setActivePersona(null); }}
                          className="absolute inset-0 w-full h-full appearance-none cursor-pointer bg-transparent z-10
                            [&::-webkit-slider-runnable-track]:bg-transparent
                            [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-[14px] [&::-webkit-slider-thumb]:h-[14px] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-foreground-400 [&::-webkit-slider-thumb]:cursor-pointer
                            [&::-moz-range-track]:bg-transparent [&::-moz-range-track]:h-1
                            [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-[14px] [&::-moz-range-thumb]:h-[14px] [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-foreground-400 [&::-moz-range-thumb]:cursor-pointer"
                        />
                      </div>
                      <div className="flex justify-between -mt-0.5">
                        <span className="text-[9px] text-foreground-600 font-mono">0.01</span>
                        <span className="text-[9px] text-foreground-600 font-mono">5.0</span>
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] font-[600] text-foreground-500 uppercase tracking-[0.06em]">Pip Value</span>
                        <span className="text-[16px] font-[700] text-foreground-200 font-mono">${pipValue.toFixed(0)}</span>
                      </div>
                      <div className="relative h-[20px] flex items-center">
                        <div className="absolute inset-x-0 h-1 rounded-full bg-foreground-800/50 overflow-hidden pointer-events-none">
                          <div className="h-full rounded-full bg-foreground-500/60 transition-all duration-200" style={{ width: `${(pipValue / 100) * 100}%` }} />
                        </div>
                        <input
                          type="range" min="1" max="100" step="1" value={pipValue}
                          onChange={(e) => { setPipValue(parseInt(e.target.value)); setActivePersona(null); }}
                          className="absolute inset-0 w-full h-full appearance-none cursor-pointer bg-transparent z-10
                            [&::-webkit-slider-runnable-track]:bg-transparent
                            [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-[14px] [&::-webkit-slider-thumb]:h-[14px] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-foreground-400 [&::-webkit-slider-thumb]:cursor-pointer
                            [&::-moz-range-track]:bg-transparent [&::-moz-range-track]:h-1
                            [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-[14px] [&::-moz-range-thumb]:h-[14px] [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-foreground-400 [&::-moz-range-thumb]:cursor-pointer"
                        />
                      </div>
                      <div className="flex justify-between -mt-0.5">
                        <span className="text-[9px] text-foreground-600 font-mono">$1</span>
                        <span className="text-[9px] text-foreground-600 font-mono">$100</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ========== RIGHT: Results ========== */}
            <div className="lg:sticky lg:top-24 flex flex-col gap-4 md:gap-6">

              {/* Total Cost Big Number */}
              <div className="bg-foreground-900 rounded-2xl overflow-hidden">
                <div className="flex items-center justify-between px-5 py-3 border-b border-foreground-800/40">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-foreground-600" />
                      <div className="w-2 h-2 rounded-full bg-foreground-600" />
                      <div className="w-2 h-2 rounded-full bg-foreground-600" />
                    </div>
                    <span className="text-[12px] font-[600] text-foreground-400 uppercase tracking-[0.08em] ml-2.5">
                      Total Trade Cost
                    </span>
                  </div>
                  <span className="text-[10px] text-foreground-600 font-mono">OUTPUT</span>
                </div>

                <div className="p-5 md:p-8 text-center">
                  <div className="text-[52px] md:text-[72px] font-[800] text-white leading-none tracking-[-0.03em] font-mono mb-3">
                    ${totalCost.toFixed(2)}
                  </div>
                  <div className="text-[13px] text-foreground-500 font-mono">
                    {lotSize.toFixed(2)} lots · {daysHeld} day{daysHeld !== 1 ? 's' : ''} held
                  </div>

                  <div className="mt-6 h-2 bg-foreground-800/50 rounded-full overflow-hidden flex">
                    {pcts.spread > 0 && (
                      <div className="h-full bg-primary-500 transition-all duration-500" style={{ width: `${Math.max(pcts.spread, 3)}%` }} />
                    )}
                    {pcts.commission > 0 && (
                      <div className="h-full bg-accent-500 transition-all duration-500" style={{ width: `${Math.max(pcts.commission, 3)}%` }} />
                    )}
                    {pcts.swap > 0 && (
                      <div className="h-full bg-secondary-500 transition-all duration-500" style={{ width: `${Math.max(pcts.swap, 3)}%` }} />
                    )}
                  </div>

                  <div className="flex items-center justify-center gap-3 md:gap-5 mt-3 text-[10px] md:text-[11px] flex-wrap">
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-sm bg-primary-500" />
                      <span className="text-foreground-400">Spread ${spreadCost.toFixed(2)}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-sm bg-accent-500" />
                      <span className="text-foreground-400">Comm ${commissionCost.toFixed(2)}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-sm bg-secondary-500" />
                      <span className="text-foreground-400">Swap {swapCost >= 0 ? '+' : ''}${swapCost.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Cost Analysis */}
              <div className="bg-foreground-900 rounded-2xl overflow-hidden">
                <div className="flex items-center px-5 py-3 border-b border-foreground-800/40">
                  <div className="flex gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-foreground-600" />
                    <div className="w-2 h-2 rounded-full bg-foreground-600" />
                    <div className="w-2 h-2 rounded-full bg-foreground-600" />
                  </div>
                  <span className="text-[11px] font-[600] text-foreground-500 uppercase tracking-[0.08em] ml-2.5">
                    Cost Analysis
                  </span>
                </div>

                <div className="p-5 md:p-6 space-y-4">
                  <div>
                    <div className="text-[10px] font-[600] text-foreground-600 uppercase tracking-[0.1em] mb-2">
                      Dominant Cost
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className={`text-[28px] font-[800] font-mono tracking-tight ${
                        dominantCost === 'Spread' ? 'text-primary-400'
                        : dominantCost === 'Commission' ? 'text-accent-400'
                        : 'text-secondary-400'
                      }`}>
                        {dominantCost}
                      </span>
                      <span className="text-[14px] text-foreground-500 font-mono">
                        {dominantPctVal.toFixed(0)}% of total
                      </span>
                    </div>
                    <div className="text-[12px] text-foreground-500 mt-1.5 leading-relaxed">
                      {daysHeld === 0
                        ? 'No holding cost — spread and commission are all that matter'
                        : daysHeld <= 1
                        ? 'Short hold — spread + commission dominate the cost breakdown'
                        : daysHeld <= 5
                        ? 'Medium hold — swap cost starts to become significant'
                        : 'Long hold — swap is overwhelmingly the biggest cost line item'}
                    </div>
                  </div>

                  <div className="w-full h-px bg-foreground-800/30" />

                  <div>
                    <div className="text-[10px] font-[600] text-foreground-600 uppercase tracking-[0.1em] mb-2">
                      Formula Applied
                    </div>
                    <div className="text-[13px] font-[600] text-foreground-200 font-mono leading-relaxed">
                      ${spreadCost.toFixed(2)}
                      <span className="text-foreground-500 mx-1">+</span>
                      ${commissionCost.toFixed(2)}
                      <span className="text-foreground-500 mx-1">{swapCost >= 0 ? '+' : '−'}</span>
                      ${Math.abs(swapCost).toFixed(2)}
                      <span className="text-foreground-500 mx-1">=</span>
                      <span className="text-white">${totalCost.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom: Breakeven */}
          <div className="mt-8 md:mt-10 bg-foreground-900 rounded-2xl overflow-hidden">
            <div className="flex items-center px-4 md:px-5 py-3 border-b border-foreground-800/40">
              <div className="flex gap-1.5">
                <div className="w-2 h-2 rounded-full bg-foreground-600" />
                <div className="w-2 h-2 rounded-full bg-foreground-600" />
                <div className="w-2 h-2 rounded-full bg-foreground-600" />
              </div>
              <span className="text-[11px] font-[600] text-foreground-500 uppercase tracking-[0.08em] ml-2.5">
                Breakeven Analysis
              </span>
            </div>

            <div className="p-4 md:p-6">
              <div className="flex flex-col gap-4">
                {/* Breakeven number — always full width on mobile */}
                <div className="flex items-baseline gap-3 px-4 py-3 rounded-xl bg-foreground-900/50 border border-foreground-800/30 self-start">
                  <span className="text-[11px] font-[600] text-foreground-500 uppercase tracking-[0.06em]">Breakeven</span>
                  <span className="text-[32px] md:text-[32px] font-[800] text-white font-mono tracking-[-0.02em] leading-none">
                    {breakevenPips.toFixed(1)}
                  </span>
                  <span className="text-[12px] text-foreground-400 font-mono">pips</span>
                </div>
                <div>
                  <div className="text-[13px] md:text-[14px] font-[600] text-foreground-200 mb-1">
                    What This Means For Your Trade
                  </div>
                  <div className="text-[12px] md:text-[13px] text-foreground-500 leading-relaxed">
                    {daysHeld === 0
                      ? `You need ${Math.ceil(breakevenPips)} pips just to cover your costs before any profit. Every pip beyond that is yours.`
                      : `You need ${Math.ceil(breakevenPips)} pips to break even on costs. That's ${breakevenPips.toFixed(1)} pips of your price target gone to fees before the trade has a chance to make money.`}
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}